import { daysUntil } from '../audit/windows.js';
import { easternIsoDay } from './taskRules.js';

/**
 * B5 (owner rulings 2026-10-25) — who has been emailed about a store or
 * task, and what the flows will send NEXT. Pure; the app and the API share
 * it. The "next step" dates MIRROR the flows (docs/flows.md — keep in step
 * when a flow's timing changes):
 *  - Flow 1 (audit clocks, daily 6:00 Eastern): the day a clock turns
 *    overdue the DM gets "audit due"; then an overdue reminder every
 *    RepeatCadenceDays (default 7) after LastOverdueReminderAt; once
 *    EscalationAfterDays (default 14) overdue, ONE escalation above the DM
 *    (LastNotifiedStatus 'escalated' afterwards).
 *  - Flow 3 (tasks, daily 7:00 Eastern): "due soon" on the exact day
 *    ThresholdDays (default 3) before the due date; overdue reminders from
 *    the day after the due date, every RepeatCadenceDays after
 *    LastOverdueReminderAt; ONE escalation to the DM on exactly
 *    EscalationAfterDays overdue.
 * A rule with no NotificationConfig row sends nothing (the flows read rules
 * by EventType only); the global pause sends nothing.
 */

export const REMINDER_EVENT_TYPES = [
  'audit_due_soon',
  'audit_due_now',
  'audit_overdue_reminder',
  'audit_escalation',
  'task_assigned',
  'task_reopened',
  'task_due_soon',
  'task_overdue',
  'task_escalation',
] as const;
export type ReminderEventType = (typeof REMINDER_EVENT_TYPES)[number];

export const REMINDER_LABELS: Record<ReminderEventType, string> = {
  audit_due_soon: 'Audit due soon',
  audit_due_now: 'Audit due',
  audit_overdue_reminder: 'Audit overdue reminder',
  audit_escalation: 'Audit escalation',
  task_assigned: 'Task assigned',
  task_reopened: 'Task reopened',
  task_due_soon: 'Task due soon',
  task_overdue: 'Task overdue reminder',
  task_escalation: 'Task escalation',
};

export function isReminderEvent(value: unknown): value is ReminderEventType {
  return typeof value === 'string' && (REMINDER_EVENT_TYPES as readonly string[]).includes(value);
}

/** The timing numbers from the Notification rules (null = no rule → never sent). */
export interface ReminderRules {
  paused: boolean;
  auditDueNow: boolean;
  auditOverdueEvery: number | null;
  auditEscalateAfter: number | null;
  taskDueSoonDays: number | null;
  taskOverdueEvery: number | null;
  taskEscalateAfter: number | null;
}

const whole = (v: unknown, fallback: number) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
};

/** From NotificationConfig rows (EventType, ThresholdDays, RepeatCadenceDays, EscalationAfterDays) — the flows' defaults when blank. */
export function rulesFrom(
  rows: readonly { eventType: string; thresholdDays?: unknown; repeatCadenceDays?: unknown; escalationAfterDays?: unknown }[],
  paused: boolean,
): ReminderRules {
  const rule = (type: string) => rows.find((r) => r.eventType === type);
  const due = rule('task_due_soon');
  const taskOverdue = rule('task_overdue');
  const taskEsc = rule('task_escalation');
  const auditOverdue = rule('audit_overdue_reminder');
  const auditEsc = rule('audit_escalation');
  return {
    paused,
    auditDueNow: !!rule('audit_due_now'),
    auditOverdueEvery: auditOverdue ? Math.max(1, whole(auditOverdue.repeatCadenceDays, 7)) : null,
    auditEscalateAfter: auditEsc ? whole(auditEsc.escalationAfterDays, 14) : null,
    taskDueSoonDays: due ? whole(due.thresholdDays, 3) : null,
    taskOverdueEvery: taskOverdue ? Math.max(1, whole(taskOverdue.repeatCadenceDays, 7)) : null,
    taskEscalateAfter: taskEsc ? whole(taskEsc.escalationAfterDays, 14) : null,
  };
}

export interface NextStep {
  kind: 'due_now' | 'reminder' | 'escalation' | 'due_soon';
  /** YYYY-MM-DD (Eastern) — the morning run that sends it. */
  date: string;
  /** Who it goes to, in words the page finishes ("the assignee", "the DM", "above the DM"). */
  to: 'assignee' | 'dm' | 'escalation';
}

const DAY = 86_400_000;
/**
 * The EASTERN day of a due date — like Flow 1 and computeWindowStatus. A
 * clock's NextDueDate is a full UTC time (completedAt + N days), so an
 * evening submission's "Nov 16 01:30Z" is Nov 15 (QA 2026-10-25); a bare
 * YYYY-MM-DD is that day.
 */
const isoDay = (d: string | null | undefined) =>
  !d ? '' : /^\d{4}-\d{2}-\d{2}$/.test(d) || !Number.isFinite(Date.parse(d)) ? d.slice(0, 10) : easternIsoDay(new Date(d));
const addDays = (iso: string, n: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);
const today = (now: Date) => easternIsoDay(now);
const EASTERN_HOUR = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: '2-digit', hourCycle: 'h23' });
/** Flow 1 runs at 6:00 Eastern, Flow 3 at 7:00 (their Recurrence). */
export const FLOW1_RUN_HOUR = 6;
export const FLOW3_RUN_HOUR = 7;
/**
 * The first morning a step can still go out: today until the flow's run
 * hour, tomorrow after it (QA 2026-10-25: "due-soon reminder … today" was
 * shown after it had already been sent).
 */
function firstRunDay(now: Date, runHour: number): string {
  const hour = Number(EASTERN_HOUR.format(now));
  return Number.isFinite(hour) && hour >= runHour ? addDays(today(now), 1) : today(now);
}
const later = (a: string, b: string) => (a > b ? a : b);
/** The Eastern day of a stamp (an ISO time). */
const dayOf = (stamp: string, now: Date) => addDays(today(now), daysUntil(stamp, now));

/**
 * What Flow 3 will send next about an open/reopened task, soonest first.
 * `lastOverdueReminderAt` = Tasks.LastOverdueReminderAt.
 */
export function nextTaskSteps(
  task: { dueDate: string; status: string; lastOverdueReminderAt?: string | null },
  rules: ReminderRules,
  now: Date,
): NextStep[] {
  const due = isoDay(task.dueDate);
  if (rules.paused || task.status === 'complete' || !/^\d{4}-\d{2}-\d{2}$/.test(due)) return [];
  const t = firstRunDay(now, FLOW3_RUN_HOUR);
  const steps: NextStep[] = [];
  if (rules.taskDueSoonDays !== null) {
    const day = addDays(due, -rules.taskDueSoonDays);
    if (day >= t) steps.push({ kind: 'due_soon', date: day, to: 'assignee' });
  }
  if (rules.taskOverdueEvery !== null) {
    const firstOverdue = addDays(due, 1);
    const last = task.lastOverdueReminderAt ? dayOf(task.lastOverdueReminderAt, now) : '';
    let day = last ? later(firstOverdue, addDays(last, rules.taskOverdueEvery)) : firstOverdue;
    day = later(day, t);
    steps.push({ kind: 'reminder', date: day, to: 'assignee' });
  }
  if (rules.taskEscalateAfter !== null) {
    const day = addDays(due, rules.taskEscalateAfter);
    if (day >= t) steps.push({ kind: 'escalation', date: day, to: 'dm' });
  }
  return steps.sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * What Flow 1 will send next about one audit clock. `lastNotifiedStatus`
 * and `lastOverdueReminderAt` are the StoreAuditStatus bookkeeping columns.
 */
export function nextAuditSteps(
  clock: {
    status: 'current' | 'due_soon' | 'overdue' | 'never';
    nextDue: string | null;
    lastNotifiedStatus?: string | null;
    lastOverdueReminderAt?: string | null;
  },
  rules: ReminderRules,
  now: Date,
): NextStep[] {
  const due = isoDay(clock.nextDue);
  if (rules.paused || clock.status === 'never' || !/^\d{4}-\d{2}-\d{2}$/.test(due)) return [];
  const t = firstRunDay(now, FLOW1_RUN_HOUR);
  const steps: NextStep[] = [];
  const escalated = (clock.lastNotifiedStatus ?? '').trim().toLowerCase() === 'escalated';
  if (clock.status !== 'overdue') {
    if (rules.auditDueNow) steps.push({ kind: 'due_now', date: later(addDays(due, 1), t), to: 'dm' });
  } else if (rules.auditOverdueEvery !== null) {
    const last = clock.lastOverdueReminderAt ? dayOf(clock.lastOverdueReminderAt, now) : '';
    steps.push({ kind: 'reminder', date: later(last ? addDays(last, rules.auditOverdueEvery) : t, t), to: 'dm' });
  }
  if (rules.auditEscalateAfter !== null && !escalated) {
    steps.push({ kind: 'escalation', date: later(addDays(due, rules.auditEscalateAfter), t), to: 'escalation' });
  }
  return steps.sort((a, b) => a.date.localeCompare(b.date));
}
